import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Divider, makeStyles, Theme, Typography } from '@material-ui/core';
import moment from 'moment';
import { DateRange, MobileDateRangePicker } from '@material-ui/pickers';
import { AutocompleteChangeReason } from '@material-ui/lab/Autocomplete/Autocomplete';

import { Page } from '../../components/Page';
import { BackAppBar } from '../../components/BackAppBar';
import { PageBody } from '../../components/PageBody';
import { FormGroup } from '../../components/FormGroup';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import { Block } from '../../components/Block';
import { RepairCardList } from './components/RepairCardList';
import { useSetting } from '../../units/setting';
import { useAlert } from '../../units/alert';
import { useLoading } from '../../units/loading';
import { api } from '../../api';
import { StatusFalseError } from '../../api/error/StatusFalseError';
import { Repair } from '../../data/Repair';
import { RepairProgressHistoryData } from '../../api/repairProgress';
import { usePartner } from '../../hooks/usePartner';
import { useProductList } from '../../hooks/useProductList';
import { Autocomplete } from '../../components/Autocomplete';
import { useProductModelOptions } from '../../hooks/useProductModelOptions';

type RepairHistoryPageProps = {};

RepairHistoryPage.defaultProps = {};

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        '& .MuiPickersDateRangePickerInput-root': {
            margin: theme.spacing(1),
        },
    },
}));

function useFormData() {
    const [formData, setFormData] = useState<RepairProgressHistoryData>({
        type: '3',
        barcode: '',
        sn_code: '',
        product_model_id: '',
        repair_date: '',
        repair_date_e: '',
        stage_name_id: '',
        line_uid: '',
        login: '',
        name: '',
        email: '',
    });

    return {
        formData,
        setFormData,
    };
}

function RepairHistoryPage(props: RepairHistoryPageProps): React.ReactElement {
    const classes = useStyles();
    const history = useHistory();
    const { setting } = useSetting();
    const alert = useAlert();
    const { setIsLoading } = useLoading();
    const { formData, setFormData } = useFormData();
    const [repairList, setRepairList] = useState<Repair[]>([]);
    const [selectedDate, setSelectedDate] = useState<DateRange<moment.Moment>>([null, null]);
    const { partner, isLoading: isPartnerLoading, partnerLineUid } = usePartner();
    const { productList } = useProductList({
        line_uid: partnerLineUid || '',
        login: partner?.email || '',
    });
    const productModelOptions = useProductModelOptions(productList);

    useEffect(() => {
        if (partner) {
            handleSubmitClick();
        }
    }, [partner]);

    useEffect(() => {
        setFormData((data) => ({
            ...data,
            repair_date: selectedDate[0] ? selectedDate[0].format('YYYY-MM-DD') : '',
            repair_date_e: selectedDate[1] ? selectedDate[1].format('YYYY-MM-DD') : '',
        }));
    }, [selectedDate[0]?.unix(), selectedDate[1]?.unix()]);

    function handleBarcodeChange(value: string, reason?: AutocompleteChangeReason | 'change') {
        setFormData((prevState) => ({
            ...prevState,
            barcode: value,
        }));
    }

    function handleSubmitClick() {
        setIsLoading(true);
        api.repairProgressHistory({
            ...formData,
            login: partner?.email || '',
            line_uid: partnerLineUid || '',
        })
            .then((resData) => {
                setIsLoading(false);
                if (Array.isArray(resData)) {
                    setRepairList(resData);
                } else {
                    setRepairList([resData]);
                }
            })
            .catch(async (e) => {
                setIsLoading(false);
                if (e instanceof StatusFalseError) {
                    const errorData = await e.response.json();
                    alert.show({
                        type: 'error',
                        title: '錯誤',
                        message: errorData.msg,
                    });
                }
            });
    }

    return (
        <Page className={classes.root}>
            <BackAppBar title="維修履歷" onBackClick={() => history.replace('/service/action')} />
            <PageBody>
                <FormGroup>
                    <Autocomplete
                        options={productList.map((item) => ({ text: `${item.barcode} - ${item.product_model_id} - ${item.sn_code}`, value: item.barcode }))}
                        label="產品辨識碼"
                        freeSolo
                        onChange={handleBarcodeChange}
                        value={formData.barcode}
                    />
                    <Autocomplete
                        options={productModelOptions}
                        label="產品型號"
                        freeSolo
                        onChange={(value) => {
                            setFormData({
                                ...formData,
                                product_model_id: value,
                            });
                        }}
                    />
                    <TextField
                        label="產品序號"
                        value={formData.sn_code}
                        onChange={(e) => setFormData({
                            ...formData,
                            sn_code: e.target.value,
                        })}
                    />
                    <MobileDateRangePicker
                        value={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        inputFormat="YYYY-MM-DD"
                        clearable
                        startText="開始日期"
                        endText="結束日期"
                        showToolbar={false}
                        renderInput={(startProps, endProps) => {
                            function renderInputValue() {
                                if (!startProps?.inputProps?.value || !endProps?.inputProps?.value) {
                                    return '';
                                }

                                return `${startProps?.inputProps?.value || ''} ~ ${endProps?.inputProps?.value || ''}`;
                            }

                            return (
                                <TextField
                                    {...startProps}
                                    label="報修日期區間"
                                    inputProps={{
                                        ...startProps.inputProps,
                                        value: renderInputValue(),
                                    }}
                                    helperText=""
                                    fullWidth
                                    style={{ margin: 0 }}
                                />
                            );
                        }}
                    />
                    <Button onClick={handleSubmitClick}>查詢資料</Button>
                </FormGroup>
                <Divider />
                <Block p={1} row justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">查詢結果</Typography>
                    <Typography variant="subtitle2" color="textSecondary">*顯示3年內或最近一次的維修資料</Typography>
                </Block>
                <RepairCardList
                    repairList={repairList}
                    onClick={(item) => history.push(`/repair/history/${item.name}`)}
                />
            </PageBody>
        </Page>
    );
}

export { RepairHistoryPage };
